import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import { BACKEND_ROOT_URL } from '../utils/urls';
import { BiPlayCircle, BiErrorCircle, BiLoaderAlt } from 'react-icons/bi';

const QuickStartVideo = () => {
    const [videoData, setVideoData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchVideo = async () => {
            try {
                console.log("Fetching walkthrough video...");
                const response = await apiClient.get('/walkthrough/active');
                console.log("Walkthrough video response:", response.data);
                if (response.data && (response.data.video_url || response.data.file_path)) {
                    setVideoData(response.data);
                } else {
                    console.log("No active video data found.");
                }
            } catch (err) {
                console.error("Failed to fetch walkthrough video:", err);
                setError("Could not load video.");
            } finally {
                setLoading(false);
            }
        };

        fetchVideo();
    }, []);

    if (loading) {
        return (
            <div className="w-full aspect-video bg-gray-100 rounded-2xl flex flex-col items-center justify-center text-gray-400">
                <BiLoaderAlt className="w-8 h-8 animate-spin mb-2" />
                <p className="text-sm">Loading Walkthrough...</p>
            </div>
        );
    }

    if (error || !videoData) {
        return (
            <div className="w-full aspect-video bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400 p-6 text-center">
                <BiErrorCircle className="w-8 h-8 mb-2" />
                <p className="text-sm font-medium">Quick Start Video coming soon!</p>
                <p className="text-xs mt-1">Our team is preparing a walkthrough for you.</p>
            </div>
        );
    }

    // Helper to transform standard video links into embeddable links
    const getEmbedUrl = (url) => {
        if (!url) return '';
        
        // YouTube transformations
        const ytRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;
        const ytMatch = url.match(ytRegex);
        if (ytMatch && ytMatch[1]) {
            return `https://www.youtube.com/embed/${ytMatch[1]}`;
        }

        // Vimeo transformations
        const vimeoRegex = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/i;
        const vimeoMatch = url.match(vimeoRegex);
        if (vimeoMatch && vimeoMatch[1]) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
        }

        return url;
    };

    // Ensure the source URL is absolute
    let videoSrc = videoData.video_type === 'upload' 
        ? (videoData.video_url.startsWith('http') ? videoData.video_url : `${BACKEND_ROOT_URL}${videoData.video_url}`)
        : videoData.video_url;

    // If it's a link, ensure it's an embed URL
    if (videoData.video_type === 'link') {
        videoSrc = getEmbedUrl(videoSrc);
    }

    return (
        <div className="w-full space-y-3">
            <div className="relative group rounded-2xl overflow-hidden shadow-lg bg-black aspect-video border-4 border-white">
                {videoData.video_type === 'link' ? (
                    <iframe
                        src={videoSrc}
                        title={videoData.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                ) : (
                    <video
                        controls
                        className="w-full h-full object-contain"
                        poster="/fav.png"
                    >
                        <source src={videoSrc} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                )}
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500 px-1">
                <BiPlayCircle className="text-blue-500 w-5 h-5" />
                <span className="font-medium">Watch: {videoData.title}</span>
            </div>
        </div>
    );
};

export default QuickStartVideo;
